import { useState, useEffect, useCallback } from 'react';
import { ThemePreference, ResolvedTheme, ThemeContextType } from '../context/ThemeContext';
import { THEMES } from '../theme/registry';

const STORAGE_KEY = 'dbmar-theme';

function getSystemTheme(): ResolvedTheme {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
    if (preference === 'browser') {
        return getSystemTheme();
    }
    return preference as ResolvedTheme;
}

function readStoredPreference(): ThemePreference {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'midnight' || stored === 'browser') {
        return stored;
    }
    return 'light';
}

/**
 * Applies theme attributes to <html>:
 *   - data-theme = the specific theme ('light' | 'dark' | 'midnight') — drives token definitions
 *   - data-mode  = the theme's mode ('light' | 'dark') — drives shared dark-mode overrides
 *
 * The dual-attribute approach means Bootstrap/PrimeReact overrides can be written once
 * using [data-mode="dark"] and apply to all dark-ish themes without selector duplication.
 */
function applyThemeToDocument(theme: ResolvedTheme) {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-mode', THEMES[theme].mode);
}

export default function useThemePreference(): ThemeContextType {
    const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
    const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(readStoredPreference()));

    const setPreference = useCallback((pref: ThemePreference) => {
        localStorage.setItem(STORAGE_KEY, pref);
        setPreferenceState(pref);
        const resolved = resolveTheme(pref);
        setTheme(resolved);
        applyThemeToDocument(resolved);
    }, []);

    // Apply theme to document on mount
    useEffect(() => {
        applyThemeToDocument(theme);
    }, [theme]);

    // Listen for system theme changes when preference is 'browser'
    useEffect(() => {
        if (preference !== 'browser') {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            const resolved = e.matches ? 'dark' : 'light';
            setTheme(resolved);
            applyThemeToDocument(resolved);
        };

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [preference]);

    return { preference, theme, setPreference };
}
